import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import tippy from "tippy.js";
import i18n from "@/i18n";

import { Node } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import Suggestion from "@tiptap/suggestion";
import {
  ReactRenderer,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";

import { cn } from "@/components/utils";
import { Input } from "@/components/ui/input";
import DialogModal from "@/components/dialogModal";
import Tooltip from "@/components/tooltip";
import {
  Calculator,
  DecimalsArrowLeft,
  DecimalsArrowRight,
  SearchX,
} from "lucide-react";
import { Trans } from "react-i18next";
import { Button } from "@/components/ui/button";

const { t } = i18n;

export const PlaceholderFieldBadge = ({
  id,
  title,
  category,
  onClick,
  className,
}) => (
  <Tooltip value={title} delayDuration={100} className="font-normal not-italic">
    <span
      onClick={onClick}
      className={cn(
        className,
        "inline-flex cursor-pointer items-center gap-1 rounded border border-blue-300 bg-blue-100 px-1 align-bottom text-blue-700",
        {
          "cursor-pointer": typeof onClick === "function",
          "border-orange-300 bg-orange-100 text-orange-700":
            category === "shares",
        },
      )}
    >
      {id}
    </span>
  </Tooltip>
);

const PlaceholderCalculationBadge = ({ value, onClick }) => (
  <div
    className={cn(
      "inline-flex items-center gap-1 rounded border border-purple-300 bg-purple-100 px-1 align-bottom text-purple-700",
      { "cursor-pointer": typeof onClick === "function" },
    )}
    onClick={onClick}
  >
    <Calculator size={14} />
    {value}
  </div>
);

export const DEFAULT_FIELDS = [
  {
    id: "nameFull",
    description: t("email_editor.placeholders.nameFull.description"),
    type: "string",
    category: "personal",
  },
  {
    id: "nameFirst",
    description: t("email_editor.placeholders.nameFirst.description"),
    type: "string",
    category: "personal",
  },
  {
    id: "nameLast",
    description: t("email_editor.placeholders.nameLast.description"),
    type: "string",
    category: "personal",
  },
  {
    id: "emailAddress",
    description: t("email_editor.placeholders.emailAddress.description"),
    type: "string",
    category: "personal",
  },
  {
    id: "phoneNumber",
    description: t("email_editor.placeholders.phoneNumber.description"),
    type: "string",
    category: "personal",
  },
  {
    id: "address",
    description: t("email_editor.placeholders.address.description"),
    type: "string",
    category: "personal",
  },
  {
    id: "zipCode",
    description: t("email_editor.placeholders.zipCode.description"),
    type: "string",
    category: "personal",
  },
  {
    id: "city",
    description: t("email_editor.placeholders.city.description"),
    type: "string",
    category: "personal",
  },
  {
    id: "country",
    description: t("email_editor.placeholders.country.description"),
    type: "string",
    category: "personal",
  },
  {
    id: "bankInfo",
    description: t("email_editor.placeholders.bankInfo.description"),
    type: "string",
    category: "personal",
  },
  {
    id: "sharesOwnedTotal",
    description: t("email_editor.placeholders.sharesOwnedTotal.description"),
    type: "number",
    category: "shares",
  },
  {
    id: "ownershipPercentageTotal",
    description: t(
      "email_editor.placeholders.ownershipPercentageTotal.description",
    ),
    type: "number",
    category: "shares",
  },
  {
    id: "capitalInvestedTotal",
    description: t(
      "email_editor.placeholders.capitalInvestedTotal.description",
    ),
    type: "number",
    category: "shares",
  },
];

export const getShareClassFields = (shareClass) => [
  {
    id: `sharesOwned_${shareClass}`,
    description: t(
      "email_editor.placeholders.shares_owned_for_type.description",
      { shareClass },
    ),
    type: "number",
    category: "shares",
  },
  {
    id: `capitalInvested_${shareClass}`,
    description: t(
      "email_editor.placeholders.capital_invested_for_type.description",
      { shareClass },
    ),
    type: "number",
    category: "shares",
  },
];

export const getPlaceholderFieldsConfig = (options = { shareTypes: [] }) => {
  return [
    ...DEFAULT_FIELDS,
    ...(options.shareTypes?.flatMap?.(getShareClassFields) ?? []),
  ];
};

const PlaceholderPluginKey = new PluginKey("PlaceholderPluginKey");

const PlaceholderNodeViewRenderer = (props) => {
  const { node, editor, extension } = props;

  const isCalculation = node.attrs.name === "calculation";
  const readOnly = extension.options.readOnly;

  const justCreated = !!(
    node.attrs.createdAt && Date.now() - node.attrs.createdAt < 2000
  );
  const [isOpen, setIsOpen] = useState(justCreated);
  const [inputValue, setInputValue] = useState(node.attrs.value);
  const [decimalPlaces, setDecimalPlaces] = useState(node.attrs.decimalPlaces);

  const inputRef = useRef(null);

  useEffect(() => {
    editor.setOptions({ editable: !readOnly && !isOpen });

    if (isOpen) {
      inputRef.current?.focus();
      setDecimalPlaces(node.attrs.decimalPlaces);
    }
  }, [isOpen]);

  const allowedFields = extension?.options?.fields?.filter?.(
    (f) => f.type === "number",
  );

  let isInputValueValid;
  try {
    // Try evaluating the input value by
    // using a default value of 10 for the placeholders.
    // (Whitespace ensures separation between variables)
    // So that sharesTotalsharesTotal doesn't become `1010`, but rather ` 10  10 `
    const interpolatedValue = allowedFields.reduce(
      (acc, field) => acc.replace(new RegExp(field.id, "g"), " 10 "),
      inputValue,
    );
    isInputValueValid = eval(interpolatedValue);
  } catch (
    error // eslint-disable-line
  ) {
    isInputValueValid = false;
  }

  const validationMessage = isInputValueValid
    ? " "
    : t("email_editor.placeholders.calculation.input.invalid");
  const validationVariant = isInputValueValid ? "" : "destructive";

  const onSave = () => {
    if (!isInputValueValid) {
      return;
    }

    if (
      inputValue !== node.attrs.value ||
      decimalPlaces !== node.attrs.decimalPlaces
    ) {
      props.updateAttributes({ value: inputValue, decimalPlaces });
    }

    setIsOpen(false);
    editor.chain().focus().run();
  };

  const field = extension.options.fields?.find(
    (field) => field.id === node.attrs.name,
  );

  return (
    <NodeViewWrapper className="inline">
      {isOpen && (
        <DialogModal
          size="sm"
          title={t("email_editor.placeholders.calculation.actions.edit")}
          onSubmit={onSave}
          onCancel={() => setIsOpen(false)}
          bodyRenderer={() => (
            <div className="flex flex-col gap-10">
              <Input
                ref={inputRef}
                validationMessage={validationMessage}
                validationVariant={validationVariant}
                className="text-center font-mono"
                value={inputValue}
                onChange={(evt) => {
                  const newInputValue = evt.target.value.replace(/[,]/g, ".");
                  setInputValue(newInputValue);
                }}
                onKeyDown={(evt) => {
                  if (evt.key === "Enter") {
                    onSave(evt);
                  }
                }}
              />
              <div className="flex items-center justify-center gap-2">
                <span>
                  {t(
                    "email_editor.placeholders.calculation.decimal_places.label",
                  )}
                  {": "}
                  <strong>
                    0{decimalPlaces > 0 ? ", " : ""}
                    {"0".repeat(decimalPlaces)}
                  </strong>
                </span>
                <Button
                  variant="outline"
                  onClick={() =>
                    setDecimalPlaces(Math.max(0, decimalPlaces - 1))
                  }
                >
                  <DecimalsArrowLeft size={16} />
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    setDecimalPlaces(Math.min(10, decimalPlaces + 1))
                  }
                >
                  <DecimalsArrowRight size={16} />
                </Button>
              </div>
              <div className="flex flex-col gap-4">
                <p className="text-center">
                  <Trans i18nKey="email_editor.placeholders.calculation.edit.hint" />
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {allowedFields.map((field) => (
                    <PlaceholderFieldBadge
                      className="transition-transform hover:scale-110"
                      key={field.id}
                      id={field.id}
                      title={field.description}
                      category={field.category}
                      onClick={() => {
                        const from = inputRef.current?.selectionStart ?? 0;
                        const to = inputRef.current?.selectionEnd;
                        const prefix =
                          from > 0 &&
                          inputValue.trim().length &&
                          inputValue.charAt(from - 1) !== " "
                            ? " "
                            : "";
                        const newValue = `${inputValue.slice(
                          0,
                          from,
                        )}${prefix}${field.id}${inputValue.slice(to)}`;

                        setInputValue(newValue);

                        setTimeout(() => {
                          inputRef.current?.focus();
                          inputRef.current?.setSelectionRange(
                            from + field.id.length + prefix.length,
                            from + field.id.length + prefix.length,
                          );
                        }, 0);
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        />
      )}
      {isCalculation ? (
        <PlaceholderCalculationBadge
          onClick={!readOnly ? () => setIsOpen(!isOpen) : undefined}
          value={node.attrs.value}
        />
      ) : (
        <PlaceholderFieldBadge
          id={node.attrs.name}
          category={field?.category}
          title={field?.description}
        />
      )}
    </NodeViewWrapper>
  );
};

export const PlaceholderSuggestionList = forwardRef((props, ref) => {
  const { items, command } = props;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef(null);

  const selectItem = (index) => {
    const item = items[index];
    if (item) command({ id: item.id, type: item.type });
  };

  useEffect(() => setSelectedIndex(0), [items]);

  const scrollItemIntoView = (index) => {
    const itemNode = listRef.current?.childNodes?.[index];
    itemNode?.scrollIntoView();
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowUp") {
        const newIndex = (selectedIndex - 1 + items.length) % items.length;
        setSelectedIndex(newIndex);
        scrollItemIntoView(newIndex);
        return true;
      }

      if (event.key === "ArrowDown") {
        const newIndex = (selectedIndex + 1) % items.length;
        setSelectedIndex(newIndex);
        scrollItemIntoView(newIndex);
        return true;
      }

      if (event.key === "Enter") {
        selectItem(selectedIndex);
        return true;
      }

      return false;
    },
  }));

  return (
    <div className="scrollbox h-full" ref={listRef}>
      {items.length === 0 && (
        <div className="flex items-center justify-center gap-2 p-4 text-slate-500">
          <SearchX size={16} /> {t("noResultsText")}
        </div>
      )}
      {items.map((item, idx) => (
        <div
          onClick={() => selectItem(idx)}
          onMouseEnter={() => setSelectedIndex(idx)}
          key={item.id}
          className={cn(
            "flex cursor-pointer flex-col items-start gap-2 rounded p-2",
            {
              "bg-slate-100": idx === selectedIndex,
            },
          )}
        >
          {item.type === "calculation" ? (
            <PlaceholderCalculationBadge id={item.id} value={item.id} />
          ) : (
            <PlaceholderFieldBadge id={item.id} category={item.category} />
          )}
          <div className="text-sm text-foreground">{item.description}</div>
        </div>
      ))}
    </div>
  );
});
PlaceholderSuggestionList.displayName = "PlaceholderSuggestionList";

export const PlaceholderValue = Node.create({
  name: "placeholder",
  group: "inline",
  inline: true,
  atom: true,

  addOptions() {
    return {
      fields: DEFAULT_FIELDS,
      record: {},
    };
  },

  addAttributes() {
    return {
      name: { default: "" },
      value: { default: "" },
      decimalPlaces: { default: 2 },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-variable]" }];
  },

  renderHTML({ node }) {
    const record = this.options.record;
    const fields = this.options.fields;
    const variable = node.attrs.name;
    const decimalPlaces = node.attrs.decimalPlaces;
    const isCalculation = variable === "calculation";

    let value, rawValue;

    if (isCalculation) {
      const allowedFields = fields
        .filter((f) => f.type === "number")
        .map((f) => f.id);

      const interpolatedCalculation = allowedFields.reduce(
        (acc, field) =>
          acc.replace(new RegExp(field, "g"), ` ${record[field] ?? 0} `),
        node.attrs.value,
      );

      try {
        const evaluationResult = eval(interpolatedCalculation);

        value = evaluationResult.toLocaleString("sv-SE", {
          minimumFractionDigits: 0,
          maximumFractionDigits: decimalPlaces,
        });
      } catch (
        error // eslint-disable-line
      ) {
        value = `Error: ${interpolatedCalculation}`;
      }
      rawValue = value;
    } else {
      const field = fields.find((f) => f.id === variable);
      rawValue = record?.[variable];

      if (field?.type === "number" && rawValue) {
        value = Number(rawValue).toLocaleString("sv-SE", {
          minimumFractionDigits: 0,
        });
      } else {
        value = String(rawValue ?? `N/A (${variable})`);
      }
    }

    return [
      "span",
      {
        "data-variable": node.attrs.name,
        class:
          typeof rawValue === "undefined" ? "bg-red-100 text-red-500" : null,
      },
      value,
    ];
  },
});

export const Placeholder = Node.create({
  name: "placeholder",
  group: "inline",
  inline: true,
  atom: true,

  addOptions() {
    return {
      fields: DEFAULT_FIELDS,
      suggestion: {
        char: "/",
        items: ({ editor, query }) => {
          const fields = editor.extensionManager.extensions.find(
            (e) => e.name === "placeholder",
          )?.options?.fields;

          const allFields = [
            {
              id: "calculation",
              description: t(
                "email_editor.placeholders.calculation.description",
              ),
              type: "calculation",
            },
            ...fields,
          ];

          return allFields.filter((field) =>
            field.id.toLowerCase().includes(query.toLowerCase()),
          );
        },
        render: () => {
          let component;
          let popup;

          return {
            onStart: (props) => {
              component = new ReactRenderer(PlaceholderSuggestionList, {
                props,
                editor: props.editor,
              });

              popup = tippy(props.editor.view.dom, {
                getReferenceClientRect: props.clientRect,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: "manual",
                boundary: document.querySelector("#radix-\\:r7\\:"),
                placement: "bottom",
                theme: "editor-placeholder-suggestions",
              });
            },
            onUpdate: (props) => {
              component.updateProps(props);
              popup.setProps({ getReferenceClientRect: props.clientRect });
            },
            onKeyDown: (props) => component.ref?.onKeyDown(props),
            onExit: () => {
              component.destroy();
              popup.destroy();
            },
          };
        },
        pluginKey: PlaceholderPluginKey,
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter;
          const overrideSpace = nodeAfter?.text?.startsWith(" ");

          if (overrideSpace) {
            range.to += 1;
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs:
                  props.type === "calculation"
                    ? {
                        name: "calculation",
                        value: "1+2+3",
                        createdAt: Date.now(),
                      }
                    : { name: props.id },
              },
              { type: "text", text: " " },
            ])
            .run();

          // get reference to `window` object from editor element, to support cross-frame JS usage
          editor.view.dom.ownerDocument.defaultView
            ?.getSelection()
            ?.collapseToEnd();
        },
      },
    };
  },

  addAttributes() {
    return {
      name: {
        default: "",
      },
      value: {
        default: "",
      },
      decimalPlaces: {
        default: 1,
      },
      createdAt: {
        default: null,
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(PlaceholderNodeViewRenderer);
  },

  parseHTML() {
    return [
      {
        tag: "span[data-variable]",
        getAttrs: (dom) => {
          return {
            name: dom.getAttribute("data-variable"),
            decimalPlaces: dom.getAttribute("data-decimal-places"),
          };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const isCalculation = node.attrs.name === "calculation";
    return [
      "span",
      {
        "data-decimal-places": node.attrs.decimalPlaces,
        "data-variable": node.attrs.name,
        class: cn(
          "border px-1 rounded font-semibold",
          isCalculation
            ? "bg-purple-200 border-purple-300 text-purple-700"
            : "bg-blue-200 border-blue-300 text-blue-700",
        ),
      },
      isCalculation ? node.attrs.value : node.attrs.name,
    ];
  },

  addCommands() {
    return {
      getPlaceholderFields: () => () => {
        return this.options.fields;
      },
      insertPlaceholderCalculation:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              name: "calculation",
              value: "1+2+3",
              createdAt: Date.now(),
            },
          });
        },
      insertPlaceholderField:
        (name) =>
        ({ commands }) => {
          return commands.insertContent({ type: this.name, attrs: { name } });
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
